/**
 * Public platform settings (logo, favicon, theme color, announcement, headline).
 * Fetched anonymously on app boot and applied to <html> as CSS variables + favicon.
 * Exposed via a React context so any page (Login, Layout, etc.) can consume it.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Resolve a possibly-relative /api/files/... URL against the backend host so
// the browser fetches it from the API (not the CDN/static host). Absolute
// URLs (http://…) are passed through untouched.
const absolutize = (u) => {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/")) return `${BACKEND}${u}`;
  return u;
};

const PlatformCtx = createContext({
  logo_url: "", favicon_url: "", theme_color: "#4A2F1E",
  homepage_headline: "Every deal, every rupee, tracked.",
  announcement_text: "", announcement_active: false,
});

export function PlatformProvider({ children }) {
  const [settings, setSettings] = useState({
    logo_url: "", favicon_url: "", theme_color: "#4A2F1E",
    homepage_headline: "Every deal, every rupee, tracked.",
    announcement_text: "", announcement_active: false,
  });

  useEffect(() => {
    axios.get(`${BACKEND}/api/platform/public-settings`)
      .then((r) => {
        const logo_url = absolutize(r.data.logo_url);
        const favicon_url = absolutize(r.data.favicon_url);
        setSettings({ ...r.data, logo_url, favicon_url });
        // Apply theme color as a CSS variable so buttons/links can pick it up.
        if (r.data.theme_color) {
          document.documentElement.style.setProperty("--brand-color", r.data.theme_color);
        }
        // Swap every existing favicon link (browsers may keep multiple sizes,
        // shortcut icon vs. apple-touch-icon) so the platform admin's mark
        // wins on every device. If no <link rel="icon"> exists yet we add one.
        if (favicon_url) {
          const links = document.querySelectorAll("link[rel~='icon'], link[rel='shortcut icon']");
          if (links.length === 0) {
            const link = document.createElement("link");
            link.rel = "icon";
            link.href = favicon_url;
            document.head.appendChild(link);
          } else {
            links.forEach((link) => { link.href = favicon_url; });
          }
        }
      })
      .catch(() => { /* anonymous fetch, fall back to defaults */ });
  }, []);

  return <PlatformCtx.Provider value={settings}>{children}</PlatformCtx.Provider>;
}

export function usePlatformSettings() {
  return useContext(PlatformCtx);
}
